import { ChangeEvent, KeyboardEvent, MutableRefObject, useEffect, useState } from "react";
import classNames from "classnames";

import { ReactComponent as SlashSVG } from "@/assets/icons/slash.svg";
import NumberInput from "@/common/NumberInput";
import { Colors } from "@/constants/colors";
import { cmToFeet, feetToCm } from "@/utils/services/heightFormatter";
import { useTheme } from "@/utils/ThemeProvider";

interface FeetInchesInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    isActive: boolean;
    autoFocus?: boolean;
    refs: MutableRefObject<HTMLInputElement[] | null[]>;
    className?: string;
}

export const FeetInchesInput = ({
    value,
    onChange,
    onKeyDown,
    isActive,
    autoFocus,
    refs,
    className,
}: FeetInchesInputProps) => {
    const { isLightTheme } = useTheme();

    const [targetFeet, setFeet] = useState<number | null>(null);
    const [targetInches, setInches] = useState<number | null>(null);

    const handleFeetChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = +e.target.value;
        setFeet(value || null);
    };

    const handleInchesChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setInches(value === "" ? null : +value);
    };

    useEffect(() => {
        const { feet, inches } = cmToFeet(value);
        setFeet(feet);
        setInches(inches);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    useEffect(() => {
        if (targetFeet !== null) {
            onChange(feetToCm(targetFeet, Number(targetInches)));
        } else {
            onChange(null);
        }
    }, [onChange, targetFeet, targetInches]);

    return (
        <div className="flex w-full items-center justify-center gap-2">
            <NumberInput
                ref={(r) => {
                    if (refs.current) {
                        refs.current[0] = r;
                    }
                }}
                type="number"
                inputMode="decimal"
                step="1"
                className={classNames("w-[74px]", className)}
                value={targetFeet}
                placeholder="6"
                onChange={handleFeetChange}
                onKeyDown={onKeyDown}
                disabled={!isActive}
                autoFocus={autoFocus}
                tabIndex={1}
            />
            <SlashSVG
                className="h-10"
                fill={isLightTheme ? Colors.NEUTRAL_COOL_GREY_400 : Colors.BLACK_300}
            />
            <NumberInput
                ref={(r) => {
                    if (refs.current) {
                        refs.current[1] = r;
                    }
                }}
                type="number"
                inputMode="decimal"
                step="1"
                className={classNames("w-[74px]", className)}
                max={12}
                value={targetInches}
                placeholder="2"
                onChange={handleInchesChange}
                onKeyDown={onKeyDown}
                disabled={!isActive}
            />
        </div>
    );
};
